import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import type { UgotProGrant, UgotProGrantList as GrantList } from '../../ugotPro/ugotProTypes';
import { UGOT_PRO_GRANT_STATE_LABELS, formatUgotProDate, formatUgotProDateTime } from '../../ugotPro/ugotProFormat';

type Props = {
  grants: GrantList | null;
  loading: boolean;
  error: string | null;
  emailByUserId: ReadonlyMap<string, string>;
  onOpenUser: (userId: string) => void;
};

function GrantTable({ rows, emailByUserId, onOpenUser, ended }: {
  rows: UgotProGrant[];
  emailByUserId: Props['emailByUserId'];
  onOpenUser: Props['onOpenUser'];
  ended: boolean;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>이메일</TableHead>
          <TableHead>메모</TableHead>
          <TableHead>{ended ? '상태' : '만료'}</TableHead>
          <TableHead>{ended ? '끝난 때' : '부여'}</TableHead>
          <TableHead className="text-right">관리</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((grant) => (
          <TableRow key={grant.userId}>
            <TableCell className="min-w-[180px] break-words">{emailByUserId.get(grant.userId) || grant.userId}</TableCell>
            <TableCell className="min-w-[96px] break-words">{grant.note || '-'}</TableCell>
            <TableCell>
              {ended
                ? <Badge variant={UGOT_PRO_GRANT_STATE_LABELS[grant.state].tone}>{UGOT_PRO_GRANT_STATE_LABELS[grant.state].label}</Badge>
                : formatUgotProDate(grant.expiresAt)}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {ended
                ? formatUgotProDateTime(grant.state === 'REVOKED' ? grant.revokedAt : grant.expiresAt)
                : `${grant.grantedBy} · ${formatUgotProDate(grant.grantedAt)}`}
            </TableCell>
            <TableCell className="text-right">
              <Button type="button" size="sm" variant="ghost" onClick={() => onOpenUser(grant.userId)}>열기</Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** 지금 유효한 내부 권한과 최근에 끝난 권한. */
export function UgotProGrantList({ grants, loading, error, emailByUserId, onOpenUser }: Props) {
  return (
    <Card data-testid="ugot-pro-grant-list">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          내부 권한을 받은 사람 <Badge variant="success">{grants?.active.length ?? 0}명</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-muted-foreground">불러오는 중…</p>}
        {error && <p className="text-sm text-red-700">{error}</p>}
        {grants && grants.active.length === 0 && <p className="text-sm text-muted-foreground">지금 유효한 내부 권한이 없습니다.</p>}
        {grants && grants.active.length > 0 && (
          <GrantTable rows={grants.active} emailByUserId={emailByUserId} onOpenUser={onOpenUser} ended={false} />
        )}
        {grants && grants.recentlyEnded.length > 0 && (
          <details>
            <summary className="cursor-pointer text-sm font-medium">최근 끝난 권한 {grants.recentlyEnded.length}건(회수·만료)</summary>
            <div className="mt-2">
              <GrantTable rows={grants.recentlyEnded} emailByUserId={emailByUserId} onOpenUser={onOpenUser} ended />
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
